/**
 * Cross-App URL Resolution
 *
 * This utility provides dynamic URL resolution for cross-linking between
 * InsightSQC and Fuel apps based on the current deployment environment.
 *
 * Production Strategy:
 * - Auto-detects client subdomain from current hostname
 * - Constructs corresponding URL for the other app
 * - Example: cvr.insightsqc.com → cvr.fuelcortex.com
 *
 * Dev/UAT Strategy:
 * - Uses explicit environment variables set in Netlify
 * - Allows overriding for specific environments
 */

/**
 * Get the Fuel app URL for cross-linking from InsightSQC
 * @returns {string|null} Fuel app URL or null if not available
 */
export const getFuelAppUrl = () => {
  // Priority 1: Explicit environment variable (overrides everything)
  if (process.env.REACT_APP_FUEL_URL) {
    return process.env.REACT_APP_FUEL_URL;
  }

  // Priority 2: Auto-detect from hostname
  const hostname = window.location.hostname;

  // DEV: Netlify preview/master builds
  if (hostname.includes('netlify.app') || hostname.includes('netlify.com')) {
    return 'https://master--fuelsqcv2.netlify.app';
  }

  // UAT: UAT environment
  if (hostname.includes('uat.')) {
    return 'https://master.uat.fuelcortex.com';
  }

  // Local development: use DEV endpoint for testing
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'https://master--fuelsqcv2.netlify.app';
  }

  // Production: Extract client subdomain from current hostname
  // Expected format: [client].fuelsqc.com or [client].insightsqc.com
  const clientMatch = hostname.match(/^([^.]+)\.(fuelsqc|insightsqc)\.com$/);

  if (clientMatch && clientMatch[1]) {
    const client = clientMatch[1];
    // Construct Fuel app URL using the same client subdomain
    return `https://${client}.fuelcortex.com`;
  }

  // No client detected - hide cross-links
  return null;
};

/**
 * Get the InsightSQC app URL for cross-linking from Fuel
 * @returns {string|null} InsightSQC app URL or null if not available
 */
export const getInsightSQCAppUrl = () => {
  // Priority 1: Explicit environment variable (overrides everything)
  if (process.env.REACT_APP_ISQC_URL) {
    return process.env.REACT_APP_ISQC_URL;
  }

  // Priority 2: Auto-detect from hostname
  const hostname = window.location.hostname;

  // DEV: Netlify preview/master builds
  if (hostname.includes('netlify.app') || hostname.includes('netlify.com')) {
    return 'https://master--insightsqc.netlify.app';
  }

  // UAT: UAT environment
  if (hostname.includes('uat.')) {
    return 'https://master.uat.insightsqc.com';
  }

  // Local development: hide cross-links unless explicitly configured
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return null;
  }

  // Production: Extract client subdomain from current hostname
  // Expected format: [client].fuelcortex.com
  const clientMatch = hostname.match(/^([^.]+)\.fuelcortex\.com$/);

  if (clientMatch && clientMatch[1]) {
    const client = clientMatch[1];
    // Construct InsightSQC app URL using the same client subdomain
    return `https://${client}.insightsqc.com`;
  }

  // No client detected - hide cross-links
  return null;
};
